import { getNestedOrFloatingElementById, getPersonIdsForField } from '@fs/zion-sls'

/**
 * Get the person data for the persons sharing this element. This is a simpler variation of a function found in other panels.
 * It assumes the element exists and is not being created.
 *
 * Supports two modes:
 * 1. Form data mode: Uses pre-computed linkedPersonIds from the calling card (reflects unsaved changes)
 *    - The calling card is responsible for extracting linked persons from its form structure
 *    - This allows each card type (Essential Info, Events, Additional Facts) to handle their own hierarchy
 *    - linkedPersonIds should be an array of person objects: [{id: string, partitionKey: string}]
 * 2. SLS mode: Queries SLS directly using getPersonIdsForField (reflects saved state)
 *
 * @param {String} selectedPersonId - id of the selected person
 * @param {Array} images - zionSlsState.images
 * @param {Object} element - SLS element or form field object to get the attached persons from
 * @param {Array<{id: string, partitionKey: string}>|Array<String>} linkedPersonIds - Optional. Pre-computed array of linked person objects [{id, partitionKey}] or person ID strings (for form mode)
 * @returns {Array} Persons sharing the element
 */
export const getPersonsFromElement = ({ selectedPersonId, images, element, linkedPersonIds = null }) => {
  if (!images) return []

  let personIds = []

  if (linkedPersonIds !== null) {
    // Form data mode: Use pre-computed person IDs from calling card
    // Handle both array of objects [{id, partitionKey}] and array of strings (backward compatibility)
    personIds = linkedPersonIds.map((item) => (typeof item === 'string' ? item : item.id))
  } else if (element) {
    // SLS mode: Query SLS directly (default behavior)
    personIds = getPersonIdsForField(element, images)
  }

  const persons = personIds.map((personId) => getNestedOrFloatingElementById(images, personId)).filter(Boolean) // Remove any null/undefined results

  persons.sort((a, b) => {
    if (a.id === selectedPersonId) return -1
    if (b.id === selectedPersonId) return 1
    return 0
  })

  return persons
}

/**
 * Sort person ids by ark. Persons sharing the element will be added to the selected and display arrays. Persons in the current record will be added to the display array.
 * We want dangling persons from the record to be displayed in the other arks section, even if they are not attached.
 * @param {Array} allPersonsSharingElement - persons who share the element
 * @param {Array} personsInThisRecord - persons in the current record. They will be added to the display array for the appropriate ark.
 * @returns {*} associative array with objects for each ark with selected and display arrays
 */
export const getSelectedAndDisplayPersonsByArk = ({ allPersonsSharingElement, personsInThisRecord }) => {
  const addPersonOrArray = (acc, person) => {
    if (!acc[person.partitionKey]) {
      acc[person.partitionKey] = { selected: [person], display: [person] }
    } else {
      acc[person.partitionKey].selected.push(person)
      acc[person.partitionKey].display.push(person)
    }
    return acc
  }

  const result = allPersonsSharingElement?.reduce(addPersonOrArray, {}) || {}
  /* e.g. { '3:1:etc': [...personsOnThisImage] }, '3:1:etc': [...personsOnOtherImage] } */

  // Add persons from record to display array if not already present (check by ID)
  personsInThisRecord.forEach((person) => {
    if (!result[person.partitionKey]) {
      result[person.partitionKey] = { selected: [], display: [person] }
    } else {
      const alreadyDisplayed = result[person.partitionKey].display.some((p) => p.id === person.id)
      if (!alreadyDisplayed) {
        result[person.partitionKey].display.push(person)
      }
    }
  })
  return result
}
